import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemIcon, Menu,
    MenuItem, TextField, Typography
} from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Archive from "@mui/icons-material/Archive";
import Unarchive from "@mui/icons-material/Unarchive";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import Timeline from "@mui/icons-material/Timeline";
import { useAsyncAction } from "nate-react-api-helpers";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/API";
import { Goal, Project, ProjectInput } from "../../api/Projects";
import { Task } from "../../api/Tasks";
import { Dropdown } from "../../misc/Dropdown";
import { FieldRow } from "../../misc/FieldRow";
import { HtmlEditor } from "../../misc/HtmlEditor";
import { panelFontSize } from "../task/panel/PanelSection";
import { randomColor } from "../../misc/projectColors";
import { snackText, useSnackAction } from "../../misc/SnackAction";
import { useSyncedRef } from "../../misc/SyncedRef";
import { TinyButton } from "../../misc/TinyButton";
import { teamName, useWorkspace } from "../../misc/Workspace";
import { TaskCrumbs, TrailStep } from "../task/panel/TaskCrumbs";
import { PanelTaskTabs } from "./PanelTaskTabs";
import { ProjectColorPicker } from "./ProjectColorPicker";
import { ProjectGoals } from "./ProjectGoals";

// let typing settle before an auto-save goes out
const textDebounceMs = 800;

export function ProjectPanel(props: {
    value: Partial<Project>;
    onClose: () => void;
    onSaved: (project: Project) => void;

    // onOpenTask hands one of the project's tasks to whoever owns the panel slot,
    // which swaps this panel out for the task editor. onOpenGoal does the same for
    // one of its goals.
    onOpenTask: (task: Task) => void;
    onOpenGoal: (goal: Goal) => void;

    // ancestors is what the page opened this panel out of, outermost first
    ancestors?: TrailStep[];
}) {
    const [name, setName] = useState(props.value.name || "");
    const [description, setDescription] = useState(props.value.description || "");
    const [color, setColor] = useState(() => props.value.color || randomColor());
    const [archived, setArchived] = useState(props.value.archived || false);
    const [code, setCode] = useState(props.value.code || "");

    const navigate = useNavigate();
    const ws = useWorkspace();
    const isNew = !props.value.id;

    // a new project goes to the workspace's team, or the personal team in "All". An
    // existing one is moved (see move below).
    const [team, setTeam] = useState(props.value.team || ws.newTeam);
    const [moveTo, setMoveTo] = useState(0);
    const [menu, setMenu] = useState<HTMLElement | null>(null);

    // the code is saved once it's whole, so a half-typed one isn't turned away
    const savedCode = code.length === 2 ? code : props.value.code || "";

    const input: ProjectInput = {
        id: props.value.id,
        name: name,
        description: description,
        color: color,
        code: isNew ? code : savedCode,
        team: team,
        archived: archived,
    };

    const inputRef = useSyncedRef(input);
    const onSavedRef = useSyncedRef(props.onSaved);

    const create = useSnackAction(async () => {
        const result = await api.projects.upsert(inputRef.current);
        onSavedRef.current(result);
    }, { success: "Saved" });

    const setArchive = useSnackAction(async (input: { archived: boolean }) => {
        const result = await api.projects.upsert({ ...inputRef.current, archived: input.archived });
        setArchived(input.archived);

        // a restored project whose code was taken in the meantime gets a new one
        setCode(result.code);
    }, { success: "Saved" });

    const move = useSnackAction(async (input: { team: number }) => {
        setMoveTo(0);

        const result = await api.projects.move({ id: props.value.id || 0, team: input.team });
        const saved = await api.projects.get({ id: props.value.id || 0 });

        setTeam(saved.team);
        setCode(saved.code);
        onSavedRef.current(saved);

        const parts = ["Moved the project and " + result.moved + (result.moved === 1 ? " task" : " tasks")];
        if (result.unassigned) parts.push(result.unassigned + " assignees removed");

        return snackText(parts.join(". "));
    }, { success: "Moved" });

    // an auto-save carrying no change is just noise, so compare against what the
    // server was last given
    const saved = useRef(JSON.stringify(input));

    const autoSave = useAsyncAction(async () => {
        const posted = inputRef.current;
        const result = await api.projects.upsert(posted);
        saved.current = JSON.stringify(posted);
        onSavedRef.current(result);
    }, []);

    const autoSaveRef = useSyncedRef(autoSave.callback);
    const saveChanged = useRef(() => {});
    saveChanged.current = () => {
        if (JSON.stringify(inputRef.current) === saved.current) return;
        autoSaveRef.current(null);
    };

    // an existing project saves as it's edited; the text fields wait for a pause in
    // typing, the colour goes out right away
    const pendingText = useRef(false);
    const startedText = useRef(false);
    useEffect(() => {
        if (isNew) return;
        if (!startedText.current) {
            startedText.current = true;
            return;
        }

        pendingText.current = true;
        const timer = setTimeout(() => {
            pendingText.current = false;
            saveChanged.current();
        }, textDebounceMs);

        return () => clearTimeout(timer);
    }, [isNew, name, description, savedCode]);

    const startedColor = useRef(false);
    useEffect(() => {
        if (isNew) return;
        if (!startedColor.current) {
            startedColor.current = true;
            return;
        }

        saveChanged.current();
    }, [isNew, color]);

    // don't drop a text edit that was still waiting when the panel closed
    useEffect(() => () => {
        if (pendingText.current) saveChanged.current();
    }, []);

    let status = "";
    if (autoSave.loading) status = "Saving...";
    else if (autoSave.error) status = autoSave.error;

    const trail = (props.ancestors || []).concat({ title: name });

    return (
        <Box style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 3, pr: 1, pt: 1 }}>
                {!isNew && (
                    <TinyButton
                        variant="outlined"
                        color="primary"
                        startIcon={<Timeline sx={{ fontSize: 16 }} />}
                        sx={{ flexShrink: 0 }}
                        onClick={() => navigate(ws.path("/projects/" + props.value.id + "/gantt"))}
                    >
                        Gantt
                    </TinyButton>
                )}
                <TaskCrumbs value={trail} />
                <Typography variant="caption" noWrap
                            color={autoSave.error ? "error.main" : "text.secondary"}
                            style={{ flex: trail.length > 1 ? undefined : 1 }}>
                    {status}
                </Typography>
                {archived && (
                    <Typography variant="caption" color="warning.dark" sx={{ flexShrink: 0 }}>
                        Archived
                    </Typography>
                )}
                <IconButton size="small" onClick={props.onClose}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, pb: 3 }}>
                <form id="project-panel" onSubmit={e => {
                    e.preventDefault();
                    create.callback(null);
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* the code sits left of the name, where the lists show it */}
                        <TextField
                            placeholder={isNew ? "Auto" : "XX"}
                            variant="standard"
                            value={code}
                            onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 2))}
                            sx={{ width: 44, flexShrink: 0 }}
                            slotProps={{
                                input: { disableUnderline: true, style: { fontSize: 20, fontWeight: 600, letterSpacing: 0.5 } },
                                htmlInput: { "aria-label": "Project code", maxLength: 2 },
                            }}
                        />
                        <TextField
                            placeholder="Project name"
                            variant="standard"
                            fullWidth
                            autoFocus
                            value={name}
                            onChange={e => setName(e.target.value)}
                            slotProps={{ input: { disableUnderline: true, style: { fontSize: 24, fontWeight: 500 } } }}
                        />
                        {!isNew && (
                            <>
                                <IconButton size="small" aria-label="Project menu" sx={{ flexShrink: 0, mr: -2 }}
                                            disabled={setArchive.loading}
                                            onClick={e => setMenu(e.currentTarget)}>
                                    <MoreHoriz fontSize="small" />
                                </IconButton>
                                <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}
                                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                                      slotProps={{ paper: { sx: { "& .MuiMenuItem-root": { fontSize: panelFontSize, minHeight: 32 } } } }}>
                                    <MenuItem onClick={() => {
                                        setMenu(null);
                                        setArchive.callback({ archived: !archived });
                                    }}>
                                        <ListItemIcon>
                                            {archived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
                                        </ListItemIcon>
                                        {archived ? "Restore" : "Archive"}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>

                    <div style={{ height: 12 }} />
                    <FieldRow label="Colour">
                        <ProjectColorPicker value={color} onChange={setColor} />
                    </FieldRow>

                    <FieldRow label="Team">
                        <Dropdown
                            value={team}
                            disabled={move.loading}
                            options={ws.teams.map(row => ({ value: row.id, label: teamName(row, row.id) }))}
                            onChange={value => {
                                if (value === team) return;
                                if (isNew) setTeam(value);
                                else setMoveTo(value);
                            }}
                        />
                    </FieldRow>
                </form>

                {!isNew && (
                    <>
                        <div style={{ height: 8 }} />
                        <ProjectGoals
                            projectId={props.value.id || 0}
                            goals={props.value.goals || []}
                            onOpenGoal={props.onOpenGoal}
                        />

                        <ProjectTasks projectId={props.value.id || 0} onOpenTask={props.onOpenTask} />
                    </>
                )}

                <div style={{ height: 16 }} />
                {/* full-bleed: cancel the panel's horizontal padding */}
                <Box sx={{ mx: -3 }}>
                    <HtmlEditor placeholder="What is this project for..."
                                fontSize={panelFontSize}
                                value={description} onChange={setDescription} />
                </Box>
            </Box>

            <Dialog open={!!moveTo} onClose={() => setMoveTo(0)} maxWidth="xs" fullWidth>
                <DialogTitle>Move this project?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ pb: 1 }}>
                        Move this project and its {props.value.openCount || 0} open tasks to{" "}
                        {teamName(ws.teams.find(row => row.id === moveTo) || null, moveTo)}?
                        Its goals and completed tasks go too.
                    </Typography>
                    <Alert severity="warning">
                        Assignees who aren't on the new team are removed from its tasks.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMoveTo(0)}>Cancel</Button>
                    <Button variant="contained" onClick={() => move.callback({ team: moveTo })}>Move</Button>
                </DialogActions>
            </Dialog>

            {isNew && (
                <Box sx={{
                    display: "flex", justifyContent: "flex-end", gap: 1, p: 2,
                    borderTop: 1, borderColor: "divider",
                }}>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button type="submit" form="project-panel" variant="contained"
                            disabled={create.loading}>Save</Button>
                </Box>
            )}
        </Box>
    );
}

// ProjectTasks lists what the project is made of, soonest first and then in the
// order the rows were dragged into.
function ProjectTasks(props: { projectId: number; onOpenTask: (task: Task) => void }) {
    const add = useSnackAction(async (input: { title: string }) => {
        await api.tasks.upsert({ title: input.title, project: props.projectId });
    }, { success: "Added" });

    // a task outlives the project it was filed under, so taking it off the list only
    // clears the project
    const unassign = useSnackAction(async (input: { task: Task }) => {
        await api.tasks.upsert({
            ...input.task,
            project: null,
        });
    }, { success: "Removed from project" });

    const reorder = useSnackAction(async (input: { ids: number[] }) => {
        await api.tasks.reorderProject(input);
    }, { success: "Saved" });

    return (
        <PanelTaskTabs
            scope={{ project: props.projectId }}
            addPlaceholder="Add a task to this project"
            removeLabel="Remove from project"
            busy={unassign.loading || reorder.loading}
            onAdd={title => add.callback({ title: title })}
            onOpen={props.onOpenTask}
            onRemove={task => unassign.callback({ task: task })}
            onReorder={ids => reorder.callback({ ids: ids })}
        />
    );
}
